/**
 * Window titles (25 Oct 2026, D-1025-NAV): every page in the shell is "<Page> · ABF Scheduler" so a screen reader, the tab
 * list and the history all say where you are. A page that names its own (a tenant's page, a volunteer's page) keeps its own:
 * `routeTitleKey` answers null for those and this hook leaves the title alone.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { PROGRAM_PATH } from './nav';

export const APP_TITLE = 'ABF Scheduler';

export interface TitleCtx {
  /** The role has a Today at all (crew, tech and the others: `/` is their one screen). */
  hasToday: boolean;
  role: string;
}

const EXACT: Record<string, string> = {
  '/housekeeping': 'programs.housekeeping',
  '/companion': 'programs.companion',
  '/reading': 'programs.reading',
  '/lookup': 'nav.lookup',
  '/schedule': 'nav.schedule',
  '/planning': 'nav.planning',
  '/planning/numbers': 'nav1025.title.numbers',
  '/calendar': 'nav.calendar',
  '/people': 'nav.people',
  '/volunteers': 'nav.volunteers',
  '/events': 'nav.events',
  '/events/month': 'nav1025.title.eventsMonth',
  '/events/surveys': 'nav1025.title.surveys',
  '/events/announcements': 'nav1025.title.announcements',
  '/messages': 'nav.messages',
  '/settings': 'nav.settings',
  '/help': 'help.title',
  '/tech': 'tech185.tab',
};

/** The i18n key of the page's name; null when the page sets its own title. */
export function routeTitleKey(pathname: string, ctx: TitleCtx): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (path === '/') {
    if (ctx.role === 'tech') return 'tech185.tab';
    if (ctx.hasToday) return 'nav.today';
    return ctx.role === 'crew' ? 'nav.today' : 'programs.housekeeping';
  }
  if (EXACT[path]) return EXACT[path]!;
  // pages with their own title (TenantPage, VolunteerPage)
  if (/^\/people\/[^/]+$/.test(path) || /^\/volunteers\/[^/]+$/.test(path)) return null;
  if (/^\/people\/[^/]+\/home-visit$/.test(path)) return 'nav1025.title.homeVisit';
  if (/^\/visits\/[^/]+$/.test(path)) return 'nav1025.title.visit';
  if (/^\/tech\/[^/]+$/.test(path)) return 'nav1025.title.ticket';
  if (/^\/events\/surveys\/[^/]+$/.test(path)) return 'nav1025.title.survey';
  if (/^\/events\/announcements\/[^/]+$/.test(path)) return 'nav1025.title.announcement';
  if (/^\/events\/[^/]+\/roster$/.test(path)) return 'nav1025.title.roster';
  if (/^\/events\/[^/]+$/.test(path)) return 'nav1025.title.event';
  if (path === '/admin' || path.startsWith('/admin/')) return 'nav.admin';
  return null;
}

export const titleOf = (page: string) => `${page} · ${APP_TITLE}`;

export function useRouteTitle(ctx: TitleCtx) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const key = routeTitleKey(pathname, ctx);
  useEffect(() => {
    if (key) document.title = titleOf(t(key));
  }, [key, i18n.language, t]);
}

// the program addresses are listed here so a test can hold the two files together
export const PROGRAM_TITLE_PATHS = Object.values(PROGRAM_PATH);
